'use client'

import { ReactNode, useContext, useState } from 'react'
import Image from 'next/image'

import {
  type DeliveryArea as DeliveryAreaValues,
  DeliveryAreaForm,
  useCheckDeliveryCoverage
} from 'modules/delivery'
import FormattedText from 'shared/ui/FormattedText'
import {
  OfflineContext,
  OfflineContextType
} from 'shared/ui/Offline/OfflineContext'
import { mobileBreakPoint } from 'shared/ui/utils/breakpoints'
import { getApiErrors } from 'shared/utils/errors'

import {
  PageTitle,
  SectionContainer,
  SectionTitle
} from '../../components/landings/styles'

import styles from './DeliveryArea.module.scss'

interface DeliveryAreaTexts {
  header: string
  subheader?: string
  description: string | ReactNode
  infoBox: string | ReactNode
  image: {
    src: string
    alt: string
  }
  form: {
    title: string
    inputLabel: string
    checkButton: string
    setPostalCodeButton: string
    successMessage: string
    errorMessage: string
    reportMissingPostalCode: string
  }
}

interface DeliveryAreaProps {
  savePostalCode?: boolean
  texts: DeliveryAreaTexts
}

const DeliveryArea = ({ savePostalCode = false, texts }: DeliveryAreaProps) => {
  const {
    update,
    isDeliveryPossible,
    postalCodeAndCityContext,
    clearDeliveryPossible
  } = useCheckDeliveryCoverage(true)

  const [isLoading, setIsLoading] = useState(false)

  const { isOnline, temporaryOfflineNotificationError } = useContext(
    OfflineContext
  ) as OfflineContextType

  const handleSubmit = async (values: DeliveryAreaValues) => {
    if (isOnline) {
      setIsLoading(true)

      try {
        await update({
          postalCode: values.postalCode,
          city: values.city
        })
      } catch (error) {
        console.error(getApiErrors(error))
      } finally {
        setIsLoading(false)
      }
    } else {
      temporaryOfflineNotificationError()
    }
  }

  return (
    <SectionContainer $condensed>
      <PageTitle>
        <FormattedText text={texts.header} />
      </PageTitle>
      <div className={styles.contentWrapper}>
        <div className={styles.leftContent}>
          <SectionTitle>
            <FormattedText text={texts.subheader} />
          </SectionTitle>
          <div className={styles.pageDescription}>{texts.description}</div>
          <div className={styles.postalCodeCheckWrapper}>
            <div className={styles.postalCodeCheck}>
              <h3 className={styles.postalCodeCheckTitle}>{texts.form.title}</h3>
              <DeliveryAreaForm
                className={styles.postalCodeForm}
                initValues={{
                  postalCode: postalCodeAndCityContext.postalCode,
                  city: postalCodeAndCityContext.city
                }}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                onChange={clearDeliveryPossible}
                buttonText={
                  savePostalCode
                    ? texts.form.setPostalCodeButton
                    : texts.form.checkButton
                }
                focusOnPostalCodeInput={false}
                deliveryPossible={isDeliveryPossible}
              />
            </div>
          </div>
        </div>
        <div className={styles.mapWrapper}>
          <Image
            src={texts.image.src}
            alt={texts.image.alt}
            fill
            quality={100}
            style={{ objectFit: 'contain', objectPosition: 'center' }}
            sizes={`(max-width: ${mobileBreakPoint}px) 100vw, 50vw`}
          />
        </div>
      </div>
    </SectionContainer>
  )
}

export default DeliveryArea
